import { useState } from 'react'
import { Droplet, Eye, EyeOff } from 'lucide-react'

export default function LoginScreen({ onLogin, error, onPasswordReset, onGoToSignup }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showReset, setShowReset] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetStatus, setResetStatus] = useState(null) // 'sent' | 'error' | null
  const [resetError, setResetError] = useState('')
  const [resetting, setResetting] = useState(false)
  const [signingIn, setSigningIn] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setSigningIn(true)
    await onLogin(email, password)
    setSigningIn(false)
  }

  async function handleReset(e) {
    e.preventDefault()
    setResetting(true)
    setResetError('')
    const result = await onPasswordReset(resetEmail)
    setResetting(false)
    if (result.success) {
      setResetStatus('sent')
    } else {
      setResetError(result.message)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Droplet className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800">PetroTrack</h1>
          <p className="text-gray-500 text-sm mt-1">Multi-Organization Petroleum Management</p>
        </div>

        {!showReset ? (
          /* ── Login form ── */
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="login-password" className="block text-sm font-medium text-gray-700">Password</label>
                <button
                  type="button"
                  onClick={() => { setShowReset(true); setResetEmail(email); setResetStatus(null) }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="w-full px-4 py-3 pr-11 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={signingIn}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-3 rounded-lg transition duration-200"
            >
              {signingIn ? 'Signing in…' : 'Sign In'}
            </button>

            {onGoToSignup && (
              <p className="text-center text-sm text-gray-500 pt-1">
                New to PetroTrack?{' '}
                <button type="button" onClick={onGoToSignup}
                  className="text-blue-600 font-medium hover:text-blue-700">
                  Create a free account
                </button>
              </p>
            )}
          </form>
        ) : (
          /* ── Forgot password form ── */
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-bold text-gray-800">Reset Password</h2>
              <p className="text-sm text-gray-500 mt-0.5">Enter your email and we'll send you a reset link.</p>
            </div>

            {resetStatus === 'sent' ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-green-800 font-semibold text-sm">Check your email</p>
                <p className="text-green-600 text-xs mt-1">A password reset link has been sent to <strong>{resetEmail}</strong></p>
              </div>
            ) : (
              <form onSubmit={handleReset} className="space-y-3">
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition text-sm"
                  required
                  autoFocus
                />
                {resetError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{resetError}</p>
                )}
                <button
                  type="submit"
                  disabled={resetting}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-3 rounded-lg transition"
                >
                  {resetting ? 'Sending…' : 'Send Reset Link'}
                </button>
              </form>
            )}

            <button
              type="button"
              onClick={() => setShowReset(false)}
              className="w-full text-sm text-gray-500 hover:text-gray-700 font-medium py-2"
            >
              ← Back to Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
